'use client';

import * as React from 'react';

export type Direction = 'ltr' | 'rtl';

/**
 * Resolves the *computed* writing direction of an element.
 * Components use it instead of assuming LTR so pointer maths (Slider) and
 * arrow-key navigation (Tabs, SegmentedControl) mirror correctly under `dir="rtl"`.
 * Falls back to `rtl` before hydration because Hebrew is the default locale.
 */
export function useElementDirection(ref: React.RefObject<HTMLElement | null>): Direction {
  const [dir, setDir] = React.useState<Direction>('rtl');
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const d = getComputedStyle(el).direction;
      setDir(d === 'rtl' ? 'rtl' : 'ltr');
    };
    read();
    const target = el.closest('[dir]') ?? document.documentElement;
    const obs = new MutationObserver(read);
    obs.observe(target, { attributes: true, attributeFilter: ['dir', 'lang'] });
    return () => obs.disconnect();
  }, [ref]);
  return dir;
}

/** Uncontrolled/controlled state in one hook. `value === undefined` → uncontrolled. */
export function useControllableState<T>(
  value: T | undefined,
  defaultValue: T,
  onChange?: (next: T) => void,
): [T, (next: T) => void] {
  const [uncontrolled, setUncontrolled] = React.useState<T>(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : uncontrolled;
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  });
  const set = React.useCallback(
    (next: T) => {
      if (!isControlled) setUncontrolled(next);
      onChangeRef.current?.(next);
    },
    [isControlled],
  );
  return [current, set];
}

/** SSR-safe unique id with a readable prefix. */
export function useId(prefix = 'k'): string {
  const id = React.useId();
  return `${prefix}-${id.replace(/:/g, '')}`;
}

/** True once mounted on the client — for portals and media queries. */
export function useMounted(): boolean {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  return mounted;
}

/** Reactive `window.matchMedia`. Returns `false` during SSR. */
export function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (cb: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', cb);
      return () => mql.removeEventListener('change', cb);
    },
    [query],
  );
  return React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** `true` on phones/tablets — Sheet uses it to decide bottom-sheet vs side-panel. */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

/** Merge any number of refs into one callback ref. */
export function useComposedRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useCallback((node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<T | null>).current = node;
    }
  }, refs);
}
